import { useState } from "react";
import styled from "styled-components";
import { BsChatDots, BsX } from "react-icons/bs";
import { devicesMax } from "../../config/devices";
import { usePortfolioChat } from "../../hooks/usePortfolioChat";
import ChatMessages from "./chat-messages";
import ChatInput from "./chat-input";
import SuggestedQuestions from "./suggested-questions";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const { messages, isStreaming, error, sendMessage, clearMessages } =
    usePortfolioChat();

  const handleToggle = () => setOpen((prev) => !prev);

  return (
    <Root>
      {open && (
        <Panel role="dialog" aria-label="Portfolio assistant chat">
          <PanelHeader>
            <HeaderText>
              <Title>Portfolio Assistant</Title>
              <Subtitle>Ask about Mark&apos;s work &amp; experience</Subtitle>
            </HeaderText>
            <HeaderActions>
              {messages.length > 0 && (
                <IconButton
                  type="button"
                  onClick={clearMessages}
                  aria-label="Clear chat"
                  title="Clear chat"
                >
                  Clear
                </IconButton>
              )}
              <IconButton
                type="button"
                onClick={handleToggle}
                aria-label="Close chat"
              >
                <BsX size={22} />
              </IconButton>
            </HeaderActions>
          </PanelHeader>

          <PanelBody>
            <ChatMessages messages={messages} isStreaming={isStreaming} />
            {messages.length === 0 && (
              <SuggestionsWrap>
                <SuggestedQuestions
                  onSelect={sendMessage}
                  disabled={isStreaming}
                />
              </SuggestionsWrap>
            )}
            {error && <ErrorText role="alert">{error}</ErrorText>}
          </PanelBody>

          <PanelFooter>
            <ChatInput onSend={sendMessage} disabled={isStreaming} />
          </PanelFooter>
        </Panel>
      )}

      <Fab
        type="button"
        onClick={handleToggle}
        aria-label={open ? "Close portfolio assistant" : "Open portfolio assistant"}
        aria-expanded={open}
      >
        {open ? <BsX size={24} /> : <BsChatDots size={22} />}
      </Fab>
    </Root>
  );
};

export default ChatWidget;

const Root = styled.div`
  position: fixed;
  right: 1.25rem;
  bottom: calc(var(--h-footer) + 1rem);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;

  @media ${devicesMax.tablet} {
    right: 0.85rem;
    bottom: calc(var(--h-footer) + 4.5rem);
  }
`;

const Panel = styled.div`
  width: min(22rem, calc(100vw - 1.7rem));
  height: min(28rem, calc(100vh - var(--h-header) - var(--h-footer) - 5rem));
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid var(--border-accent);
  background: var(--surface-glass);
  backdrop-filter: blur(12px);
  box-shadow: 0 12px 40px var(--shadow-medium);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-elevated);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--white-dart);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.45rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.72rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    color: var(--green);
    background: var(--accent-bg-hover);
  }
`;

const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  overflow: hidden;
`;

const SuggestionsWrap = styled.div`
  margin-top: auto;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #ff8a8a;
`;

const PanelFooter = styled.div`
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--border-subtle);
`;

const Fab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  border: 1px solid var(--border-accent);
  background: var(--secondary-color);
  color: var(--green);
  box-shadow: 0 8px 24px var(--shadow-soft);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--accent-bg-hover);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--green);
    outline-offset: 2px;
  }
`;
